import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import { useRef } from "react";
import { Link } from "react-router-dom";

import { AssistantBeacon } from "../components/chat/AssistantBeacon";
import { ChatPanel } from "../components/chat/ChatPanel";
import { EngineerCharacter } from "../components/hero/EngineerCharacter";
import { TrainingIntro } from "../components/hero/TrainingIntro";
import { ArrowIcon } from "../components/Icons";
import { Shell } from "../components/Shell";
import { Failed, Loading } from "../components/States";
import { useApi } from "../hooks/useApi";
import { useMagnetic } from "../hooks/useMagnetic";
import { api } from "../lib/api";

/**
 * The homepage.
 *
 * **The hero IS the page.** One screen: the claim, the network, the
 * character, and the robot that arrives to offer a conversation. No project
 * list, no testimonials, no closing pitch — every one of those made a visitor
 * read something twice and pushed the one thing this page exists for into
 * being a third of a scroll. Work lives on /projects, in full, once.
 *
 * Four depth planes in the hero, each moving at a different rate, which is
 * the reason it reads as space rather than as layers:
 *
 *   1. the WebGL network      — its own rotation, pointer-displaced
 *   2. the character          — slow scroll drift, leans toward the pointer
 *   3. the pointer spotlight  — tracks 1:1, lagged by a spring
 *   4. the type               — fastest on scroll, still on pointer
 *
 * Type does not move with the pointer, deliberately. Everything else can
 * drift; the thing being read stays where it was put.
 */

const HERO_LINE_DELAY = 0.11;

export function Home() {
  const profile = useApi(() => api.profile(), []);
  const reduced = useReducedMotion() ?? false;

  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Normalised pointer position across the hero, -0.5 … 0.5. Shared by the
  // character and the spotlight so the two planes agree about where you are.
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);

  // The spotlight lags the cursor. Chasing exactly is what makes these look
  // like a CSS demo; the lag is what makes it feel like light with weight.
  const glowX = useSpring(pointerX, { stiffness: 60, damping: 22 });
  const glowY = useSpring(pointerY, { stiffness: 60, damping: 22 });
  const glowLeft = useTransform(glowX, [-0.5, 0.5], ["18%", "82%"]);
  const glowTop = useTransform(glowY, [-0.5, 0.5], ["16%", "84%"]);
  const spotlight = useMotionTemplate`radial-gradient(28rem 28rem at ${glowLeft} ${glowTop}, color-mix(in oklab, var(--color-glow) 16%, transparent), transparent 70%)`;

  const textDrift = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const textFade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const seeWork = useMagnetic(7);

  function onHeroPointerMove(event: React.PointerEvent<HTMLElement>) {
    if (reduced) return;
    const box = event.currentTarget.getBoundingClientRect();
    pointerX.set((event.clientX - box.left) / box.width - 0.5);
    pointerY.set((event.clientY - box.top) / box.height - 0.5);
  }

  if (profile.loading) {
    return (
      <Shell className="py-24">
        <Loading label="the homepage" />
      </Shell>
    );
  }
  if (profile.error) {
    return (
      <Shell className="py-24">
        <Failed error={profile.error} what="this page" />
      </Shell>
    );
  }
  if (!profile.data) return null;

  const { hero_tagline, headline } = profile.data;

  const line = (index: number) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 26, filter: "blur(8px)" },
          animate: { opacity: 1, y: 0, filter: "blur(0px)" },
          transition: {
            duration: 0.9,
            delay: index * HERO_LINE_DELAY,
            ease: [0.22, 1, 0.36, 1] as const,
          },
        };

  return (
    <div>
      <TrainingIntro />

      <section
        ref={heroRef}
        onPointerMove={onHeroPointerMove}
        // Exactly the viewport minus the sticky header. `92svh` left a
        // sliver of dead scroll under a one-screen page, which is worse
        // than either a full screen or a real second section.
        className="relative flex min-h-[calc(100svh-4.5rem)] items-center overflow-hidden"
      >
        <EngineerCharacter
          scrollProgress={scrollYProgress}
          pointerX={pointerX}
          pointerY={pointerY}
        />

        {/* The spotlight sits above the network and below the scrim, so it
            lights the field without ever washing out the type. */}
        {!reduced && (
          <motion.div
            aria-hidden="true"
            style={{ backgroundImage: spotlight }}
            className="pointer-events-none absolute inset-0"
          />
        )}

        {/* One wash, and a light one.
            This used to be two: a flat 42% veil plus a left gradient up to
            70%. Both are gone or reduced now that the glass text column below
            carries the headline — stacking three scrims made the hero read as
            fogged rather than as depth, and dimmed the field the site is
            actually about. What remains is deliberately partial: an opaque
            scrim erases the thing it was meant to sit on. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[color-mix(in_oklab,var(--color-canvas)_45%,transparent)]"
        />

        <motion.div
          style={reduced ? undefined : { y: textDrift, opacity: textFade }}
          className="relative w-full"
        >
          <Shell className="py-10 sm:py-14">
            {/* The sheet spans the whole row, so the headline and the robot
                sit in one card rather than the robot floating outside it. */}
            <div className="relative">
              {/* The sheet behind the type. Opaque enough to carry 17px body
                  copy over a lattice of wires, translucent enough that the
                  liquid still moves behind it — which is the whole effect. */}
              <div
                aria-hidden="true"
                className="glass-strong absolute -inset-x-6 -inset-y-8 rounded-3xl sm:-inset-x-10"
              />
              {/* The copy is capped to the left of the card once the robot
                  appears (`lg`), so the headline can never run underneath
                  him on a narrow desktop or a short laptop window. The card
                  stays full width; only the text inside it is bounded. */}
              <div className="relative lg:max-w-[52%] xl:max-w-[46%]">
              <motion.p
              {...line(0)}
              className="mb-6 font-mono text-2xs tracking-[0.28em] text-accent uppercase"
            >
              AI Engineer
            </motion.p>

            <motion.h1
              {...line(1)}
              className="max-w-[15ch] text-3xl leading-[1.04] sm:text-4xl lg:max-w-[13ch] lg:text-[5.2rem] [@media(max-height:880px)]:lg:text-[4.1rem]"
            >
              {hero_tagline}
            </motion.h1>

            <motion.p {...line(2)} className="mt-6 max-w-[46ch] text-lg text-text-dim [@media(max-height:880px)]:mt-4">
              {headline}
            </motion.p>

            {/* Two actions, and that is the list. A raw email address and a
                "GitHub" link beside them made four things to read and left
                the visitor to work out which one was the point. Both routes
                still exist on the contact page, which is where someone goes
                once they have decided. */}
            <motion.div
              {...line(3)}
              className="mt-10 flex flex-wrap items-center gap-4 text-xs [@media(max-height:880px)]:mt-7"
            >
              <motion.span {...seeWork.handlers} style={seeWork.style} className="inline-block">
                <Link
                  to="/projects"
                  className="group inline-flex items-center gap-2 rounded-full border border-accent-dim bg-accent/[0.06] px-6 py-3 text-accent transition-colors duration-300 hover:border-accent hover:bg-accent/10"
                >
                  See my work
                  <ArrowIcon className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </motion.span>

              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full border border-edge px-6 py-3 text-text transition-colors duration-300 hover:border-accent-dim"
              >
                Let&rsquo;s connect
                <ArrowIcon className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
              </div>
            </div>
          </Shell>
        </motion.div>
      </section>

      <AssistantBeacon />
      <ChatPanel />
    </div>
  );
}
